import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ChatIcon from '@mui/icons-material/Chat';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SearchIcon from '@mui/icons-material/Search';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import { alpha } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import MuiTooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { RefreshIcon } from 'assets/icons';
import CajaTooltip from 'components/UI/CajaTooltip';
import moment from 'moment';
import { type FC, type ReactNode, useMemo, useState } from 'react';
import type { SellerDashboardLeadRow, SellerDashboardSellerRow } from 'services/api/leads.api';
import { useGetSellerDashboardQuery } from 'services/api/sellerDashboard.api';
import { useAssignClientToUserMutation, useGetAssignableUsersMutation } from 'services/api/user.api';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  LIGHT_GREY,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  STATUS_WARN,
  SURFACE,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  SURFACE_HOVER,
  SURFACE_TINT,
  YELLOW,
} from 'styles/tokens';
import { INSUREX_BUSINESS_UNIT_ID, SALFA_USED_ID } from 'utils/constants';

const FONT = 'Jost, sans-serif';
const NAVY_MUTED = 'rgba(21, 24, 58, 0.65)';
const NAVY_FAINT = 'rgba(21, 24, 58, 0.45)';
const NAVY_HAIR = 'rgba(21, 24, 58, 0.12)';
const DANGER = '#FF4D4F';
const DANGER_BG = '#FFE5E5';
const DANGER_FAINT_BG = '#FFF5F5';

const URGENT_RULES_TEXT =
  'Un lead se marca urgente si' +
  'el último mensaje es del cliente y lleva más de 1 hora sin respuesta del ejecutivo, ' +
  'hay 4 o más mensajes del cliente sin respuesta del ejecutivo, ' +
  'la IA detectó el tag ENOJADO o APURADO en la conversación';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const formatDateTime = (isoDate: string | null): string => {
  if (!isoDate) return '–';
  return moment(isoDate).format('DD MMM YYYY, HH:mm');
};

const elapsed = (isoDate: string | null): string => {
  if (!isoDate) return '–';
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const totalMins = Math.floor(diffMs / 60000);
  if (totalMins < 1) return 'hace <1m';
  if (totalMins < 60) return `hace ${totalMins}m`;
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  if (hours < 24) return mins > 0 ? `hace ${hours}h ${mins}m` : `hace ${hours}h`;
  const days = Math.floor(hours / 24);
  const remainHours = hours % 24;
  return remainHours > 0 ? `hace ${days}d ${remainHours}h` : `hace ${days}d`;
};

const formatDuration = (secs: number | null): string => {
  if (secs === null) return '–';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remainMins = mins % 60;
  if (hours < 24) return remainMins > 0 ? `${hours}h ${remainMins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remainHours = hours % 24;
  return remainHours > 0 ? `${days}d ${remainHours}h` : `${days}d`;
};

const formatMins = (mins: number | null | undefined): string => {
  const n = Number(mins);
  if (!Number.isFinite(n) || n <= 0) return '–';
  return formatDuration(Math.round(n * 60));
};

const timeBetween = (from: string | null, to: string | null): string => {
  if (!from || !to) return '–';
  const diffMs = new Date(to).getTime() - new Date(from).getTime();
  if (diffMs < 0) return '–';
  const secs = Math.floor(diffMs / 1000);
  return formatDuration(secs);
};

const copyToClipboard = (value: string): void => {
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(value).catch(() => {
      /* noop */
    });
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

type SortKey =
  | 'createdAt'
  | 'assignedAt'
  | 'crmUpdatedAt'
  | 'closedAt'
  | 'firstMsg'
  | 'avgResponse'
  | 'lastMessageAt'
  | 'sellerMessageCount'
  | 'totalMessageCount';

type SortDir = 'asc' | 'desc';

interface SortState {
  key: SortKey;
  dir: SortDir;
}

interface ColumnHeaderProps {
  label: string;
  tooltip?: string;
  align?: 'left' | 'center' | 'right';
  sortKey?: SortKey;
  sort?: SortState | null;
  onSort?: (key: SortKey) => void;
}

const ColumnHeader: FC<ColumnHeaderProps> = ({ label, tooltip, align = 'left', sortKey, sort, onSort }) => {
  const sortable = !!sortKey && !!onSort;
  const active = sortable && sort?.key === sortKey;
  const arrow = !sortable ? null : active ? (
    sort?.dir === 'desc' ? (
      <ArrowDownwardIcon sx={{ fontSize: '1rem', ml: 0.3, color: NAVY }} />
    ) : (
      <ArrowUpwardIcon sx={{ fontSize: '1rem', ml: 0.3, color: NAVY }} />
    )
  ) : (
    <ImportExportIcon sx={{ fontSize: '1rem', ml: 0.3, color: NAVY_FAINT, opacity: 0.7 }} />
  );

  const cell = (
    <TableCell
      align={align}
      onClick={sortable ? () => onSort?.(sortKey) : undefined}
      sx={{
        color: NAVY,
        fontFamily: FONT,
        fontWeight: 700,
        fontSize: '0.65rem',
        lineHeight: 1.15,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        py: 0.75,
        px: 0.75,
        borderBottom: 'none',
        whiteSpace: 'normal',
        wordBreak: 'normal',
        overflowWrap: 'normal',
        background: active ? SURFACE_HOVER : SURFACE_CONTAINER_LOW,
        cursor: sortable ? 'pointer' : tooltip ? 'help' : 'default',
        userSelect: 'none',
        '&:hover': sortable ? { background: SURFACE_HOVER } : undefined,
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
          width: '100%',
          gap: 0.25,
        }}
      >
        <Box component="span" sx={{ display: 'inline-block' }}>
          {label}
        </Box>
        {arrow}
      </Box>
    </TableCell>
  );
  if (!tooltip) return cell;
  return (
    <CajaTooltip title={tooltip} arrow placement="top">
      {cell}
    </CajaTooltip>
  );
};

const DateCellContent: FC<{ value: string; tooltip: string | null }> = ({ value, tooltip }) => {
  const text = (
    <Typography
      component="span"
      sx={{
        fontFamily: FONT,
        fontSize: '0.8rem',
        color: NAVY_MUTED,
        cursor: tooltip ? 'help' : 'default',
      }}
    >
      {value}
    </Typography>
  );
  if (!tooltip) return text;
  return (
    <MuiTooltip
      title={tooltip}
      arrow
      placement="top"
      slotProps={{
        tooltip: {
          sx: {
            fontFamily: FONT,
            fontSize: '0.7rem',
            bgcolor: NAVY,
            borderRadius: '6px',
            px: 1,
            py: 0.5,
          },
        },
        arrow: { sx: { color: NAVY } },
      }}
    >
      {text}
    </MuiTooltip>
  );
};

const DataCell: FC<{ children: ReactNode; align?: 'left' | 'center' | 'right'; sx?: Record<string, unknown> }> = ({
  children,
  align = 'left',
  sx,
}) => (
  <TableCell
    align={align}
    sx={{
      fontFamily: FONT,
      fontSize: '0.82rem',
      color: NAVY,
      py: 1,
      px: 0.75,
      borderBottom: 'none',
      ...sx,
    }}
  >
    {children}
  </TableCell>
);

interface LeadsTableProps {
  leads: SellerDashboardLeadRow[];
  onReassign: (clientId: number) => void;
  hideUrgent?: boolean;
  showCrmUpload?: boolean;
}

const dateValue = (iso: string | null): number => (iso ? new Date(iso).getTime() : Number.NaN);

const durationMs = (from: string | null, to: string | null): number => {
  if (!from || !to) return Number.NaN;
  const diff = new Date(to).getTime() - new Date(from).getTime();
  return diff < 0 ? Number.NaN : diff;
};

const getSortValue = (lead: SellerDashboardLeadRow, key: SortKey): number => {
  switch (key) {
    case 'createdAt':
      return dateValue(lead.createdAt);
    case 'assignedAt':
      return dateValue(lead.assignedAt);
    case 'crmUpdatedAt':
      return dateValue(lead.crmUpdatedAt);
    case 'closedAt':
      return dateValue(lead.closedAt);
    case 'firstMsg':
      return durationMs(lead.assignedAt, lead.firstSellerMessageAt);
    case 'avgResponse':
      return lead.avgResponseTimeSecs ?? Number.NaN;
    case 'lastMessageAt':
      return dateValue(lead.lastMessageAt);
    case 'sellerMessageCount':
      return lead.sellerMessageCount;
    case 'totalMessageCount':
      return lead.totalMessageCount;
  }
};

const sortLeads = (leads: SellerDashboardLeadRow[], sort: SortState | null): SellerDashboardLeadRow[] => {
  if (!sort) return leads;
  const copy = [...leads];
  copy.sort((a, b) => {
    const av = getSortValue(a, sort.key);
    const bv = getSortValue(b, sort.key);
    const aNan = Number.isNaN(av);
    const bNan = Number.isNaN(bv);
    if (aNan && bNan) return 0;
    if (aNan) return 1;
    if (bNan) return -1;
    return sort.dir === 'asc' ? av - bv : bv - av;
  });
  return copy;
};

const LeadsTable: FC<LeadsTableProps> = ({ leads, onReassign, hideUrgent = false, showCrmUpload = false }) => {
  const [sort, setSort] = useState<SortState | null>(null);
  const handleSort = (key: SortKey) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'desc' };
      if (prev.dir === 'desc') return { key, dir: 'asc' };
      return null;
    });
  };
  const sortedLeads = useMemo(() => sortLeads(leads, sort), [leads, sort]);

  return (
    <Box
      sx={{
        mx: { xs: 1, md: 2 },
        mb: 1,
        borderRadius: RADIUS_CARD,
        background: SURFACE_CONTAINER_LOWEST,
        boxShadow: SHADOW,
        overflow: 'auto',
        maxHeight: { xs: '60vh', md: '70vh' },
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { background: NAVY_HAIR, borderRadius: 3 },
      }}
    >
      <Table
        size="small"
        stickyHeader
        sx={{
          width: '100%',
          tableLayout: 'auto',
          '& th:first-of-type, & td:first-of-type': { pl: 2 },
          '& th:last-of-type, & td:last-of-type': { pr: 2 },
        }}
      >
        <TableHead>
          <TableRow sx={{ '& th': { background: SURFACE_CONTAINER_LOW } }}>
            <ColumnHeader
              label="Nombre"
              tooltip="Cliente del lead: nombre, ID y teléfono. Los chips muestran el estado: 'cerrada' (lead CLOSED), 'por responder' (mensajes del cliente sin respuesta del ejecutivo) y 'urgente' (lead que requiere atención inmediata)."
            />
            <ColumnHeader
              label="Creado"
              tooltip="Fecha y hora en que el lead se creó en Relif. Pasa el cursor sobre el valor para ver el tiempo transcurrido."
              sortKey="createdAt"
              sort={sort}
              onSort={handleSort}
            />
            <ColumnHeader
              label="Asignado"
              tooltip="Fecha y hora en que el lead se asignó al ejecutivo (transición a ASSIGNED_TO_SALESMAN, PENDING_TO_BE_ASSIGNED, o PENDING_TO_BE_HANDLED en Salfa Used / Salfa Rac Externo)."
              sortKey="assignedAt"
              sort={sort}
              onSort={handleSort}
            />
            {showCrmUpload && (
              <ColumnHeader
                label="Subida al CRM"
                tooltip="Fecha y hora en que el lead se subió el lead al CRM. Pasa el cursor sobre el valor para ver el tiempo transcurrido."
                sortKey="crmUpdatedAt"
                sort={sort}
                onSort={handleSort}
              />
            )}
            <ColumnHeader
              label="Fecha Cierre"
              tooltip="Fecha y hora en que el lead pasó a estado CLOSED. Pasa el cursor sobre el valor para ver cuánto duró la gestión (asignación → cierre)."
              sortKey="closedAt"
              sort={sort}
              onSort={handleSort}
            />
            <ColumnHeader
              label="1er Msg"
              tooltip="Tiempo desde que el lead fue asignado hasta el primer mensaje enviado por el ejecutivo."
              sortKey="firstMsg"
              sort={sort}
              onSort={handleSort}
            />
            <ColumnHeader
              label="Resp. Prom."
              tooltip="Tiempo promedio que tarda el ejecutivo en responder. Se mide desde el primer mensaje del cliente en una racha hasta la siguiente respuesta del ejecutivo."
              sortKey="avgResponse"
              sort={sort}
              onSort={handleSort}
            />
            <ColumnHeader
              label="Último Mensaje"
              tooltip="Tiempo transcurrido desde el último mensaje de la conversación, sin importar si lo envió el cliente o el ejecutivo."
              sortKey="lastMessageAt"
              sort={sort}
              onSort={handleSort}
            />
            <ColumnHeader
              label="Mensajes"
              align="center"
              tooltip="Mensajes enviados por el ejecutivo dentro de la ventana de gestión (asignación → cierre, o hasta ahora si sigue abierto)."
              sortKey="sellerMessageCount"
              sort={sort}
              onSort={handleSort}
            />
            <ColumnHeader
              label="Mensajes Totales"
              align="center"
              tooltip="Mensajes intercambiados entre cliente y ejecutivo dentro de la ventana de gestión. Excluye mensajes de la IA."
              sortKey="totalMessageCount"
              sort={sort}
              onSort={handleSort}
            />
            <ColumnHeader label="Acciones" tooltip="Abrir el chat del cliente o reasignar el lead a otro ejecutivo." />
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedLeads.map((lead, idx) => (
            <TableRow
              key={lead.id}
              sx={{
                background: idx % 2 === 0 ? SURFACE_CONTAINER_LOWEST : SURFACE,
                '&:hover': { background: SURFACE_HOVER },
                transition: 'background 0.15s',
              }}
            >
              <DataCell sx={{ width: '100%', minWidth: 220 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 0.8, rowGap: 0.4 }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 600, fontSize: '0.85rem', color: NAVY }}>
                      {lead.clientName}
                    </Typography>
                    {lead.isUrgent && lead.isOpen && !hideUrgent && (
                      <MuiTooltip
                        title={URGENT_RULES_TEXT}
                        arrow
                        placement="top"
                        slotProps={{
                          tooltip: {
                            sx: {
                              fontFamily: FONT,
                              fontSize: '0.7rem',
                              bgcolor: NAVY,
                              borderRadius: '6px',
                              px: 1,
                              py: 0.5,
                              maxWidth: 320,
                            },
                          },
                          arrow: { sx: { color: NAVY } },
                        }}
                      >
                        <Chip
                          label="urgente"
                          size="small"
                          sx={{
                            height: 18,
                            fontFamily: FONT,
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            letterSpacing: '0.05em',
                            background: DANGER,
                            color: SURFACE_CONTAINER_LOWEST,
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'help',
                            '& .MuiChip-label': { px: 0.8 },
                          }}
                        />
                      </MuiTooltip>
                    )}
                    {!lead.isOpen && (
                      <Chip
                        label="cerrada"
                        size="small"
                        sx={{
                          height: 18,
                          fontFamily: FONT,
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          letterSpacing: '0.05em',
                          background: LIGHT_GREY,
                          color: NAVY_MUTED,
                          border: 'none',
                          borderRadius: '6px',
                          '& .MuiChip-label': { px: 0.8 },
                        }}
                      />
                    )}
                    {lead.isOpen && lead.unansweredClientMessageCount > 0 && (
                      <MuiTooltip
                        title={`${lead.unansweredClientMessageCount} mensaje${
                          lead.unansweredClientMessageCount === 1 ? '' : 's'
                        } del cliente sin respuesta del ejecutivo`}
                        arrow
                        placement="top"
                        slotProps={{
                          tooltip: {
                            sx: {
                              fontFamily: FONT,
                              fontSize: '0.7rem',
                              bgcolor: NAVY,
                              borderRadius: '6px',
                              px: 1,
                              py: 0.5,
                            },
                          },
                          arrow: { sx: { color: NAVY } },
                        }}
                      >
                        <Chip
                          label="por responder"
                          size="small"
                          sx={{
                            height: 18,
                            fontFamily: FONT,
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            letterSpacing: '0.05em',
                            background: 'rgba(207, 92, 9, 0.12)',
                            color: STATUS_WARN,
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'help',
                            '& .MuiChip-label': { px: 0.8 },
                          }}
                        />
                      </MuiTooltip>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'nowrap',
                      gap: 0.4,
                      color: NAVY_FAINT,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.7rem', lineHeight: 1 }}>
                      ID #{lead.clientId ?? '–'}
                    </Typography>
                    {lead.clientId !== null && (
                      <CajaTooltip title="Copiar ID" arrow placement="top">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(String(lead.clientId));
                          }}
                          sx={{ p: 0.2, color: NAVY_FAINT, '&:hover': { color: NAVY } }}
                        >
                          <ContentCopyIcon sx={{ fontSize: '0.75rem' }} />
                        </IconButton>
                      </CajaTooltip>
                    )}
                    {lead.clientPhone && (
                      <>
                        <Typography sx={{ fontFamily: FONT, fontSize: '0.7rem', px: 0.3, lineHeight: 1 }}>·</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: '0.7rem', lineHeight: 1 }}>
                          {lead.clientPhone}
                        </Typography>
                        <CajaTooltip title="Copiar teléfono" arrow placement="top">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(lead.clientPhone ?? '');
                            }}
                            sx={{ p: 0.2, color: NAVY_FAINT, '&:hover': { color: NAVY } }}
                          >
                            <ContentCopyIcon sx={{ fontSize: '0.75rem' }} />
                          </IconButton>
                        </CajaTooltip>
                      </>
                    )}
                  </Box>
                </Box>
              </DataCell>
              <DataCell>
                <DateCellContent
                  value={formatDateTime(lead.createdAt)}
                  tooltip={lead.createdAt ? elapsed(lead.createdAt) : null}
                />
              </DataCell>
              <DataCell>
                <DateCellContent
                  value={formatDateTime(lead.assignedAt)}
                  tooltip={lead.assignedAt ? elapsed(lead.assignedAt) : null}
                />
              </DataCell>
              {showCrmUpload && (
                <DataCell>
                  <DateCellContent
                    value={formatDateTime(lead.crmUpdatedAt)}
                    tooltip={lead.crmUpdatedAt ? elapsed(lead.crmUpdatedAt) : null}
                  />
                </DataCell>
              )}
              <DataCell>
                <DateCellContent
                  value={formatDateTime(lead.closedAt)}
                  tooltip={lead.assignedAt && lead.closedAt ? timeBetween(lead.assignedAt, lead.closedAt) : null}
                />
              </DataCell>
              <DataCell>
                <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: NAVY_MUTED }}>
                  {timeBetween(lead.assignedAt, lead.firstSellerMessageAt)}
                </Typography>
              </DataCell>
              <DataCell>
                <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: NAVY_MUTED }}>
                  {formatDuration(lead.avgResponseTimeSecs)}
                </Typography>
              </DataCell>
              <DataCell>
                <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: NAVY_MUTED }}>
                  {elapsed(lead.lastMessageAt)}
                </Typography>
              </DataCell>
              <DataCell align="center">
                <Typography sx={{ fontFamily: FONT, fontWeight: 600, fontSize: '0.85rem' }}>
                  {lead.sellerMessageCount}
                </Typography>
              </DataCell>
              <DataCell align="center">
                <Typography sx={{ fontFamily: FONT, fontWeight: 600, fontSize: '0.85rem' }}>
                  {lead.totalMessageCount}
                </Typography>
              </DataCell>
              <DataCell sx={{ whiteSpace: 'nowrap', width: 1 }}>
                <Box sx={{ display: 'inline-flex', flexWrap: 'nowrap', gap: 0.4 }}>
                  {lead.clientId && (
                    <CajaTooltip title="Abrir el chat del cliente en una nueva pestaña" arrow placement="top">
                      <IconButton
                        size="small"
                        onClick={() => window.open(`/clients/${lead.clientId}`, '_blank')}
                        sx={{
                          color: NAVY,
                          background: SURFACE_CONTAINER_LOW,
                          borderRadius: RADIUS_ITEM,
                          width: 28,
                          height: 28,
                          '&:hover': { background: SURFACE_HOVER },
                        }}
                      >
                        <ChatIcon sx={{ fontSize: '0.95rem' }} />
                      </IconButton>
                    </CajaTooltip>
                  )}
                  <CajaTooltip title="Reasignar el lead a otro ejecutivo" arrow placement="top">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!lead.clientId}
                        onClick={() => lead.clientId && onReassign(lead.clientId)}
                        sx={{
                          color: NAVY,
                          background: SURFACE_CONTAINER_LOW,
                          borderRadius: RADIUS_ITEM,
                          width: 28,
                          height: 28,
                          '&:hover': { background: SURFACE_HOVER },
                          '&.Mui-disabled': { opacity: 0.4 },
                        }}
                      >
                        <SwapHorizIcon sx={{ fontSize: '0.95rem' }} />
                      </IconButton>
                    </span>
                  </CajaTooltip>
                </Box>
              </DataCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

interface MetricBoxProps {
  label: string;
  value: string;
  tooltip: string;
}

const MetricBox: FC<MetricBoxProps> = ({ label, value, tooltip }) => (
  <CajaTooltip title={tooltip} arrow placement="top">
    <Box sx={{ textAlign: 'center', minWidth: 84, flexShrink: 0, cursor: 'help' }}>
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: '0.6rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: NAVY_FAINT,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1rem', color: NAVY }}>{value}</Typography>
    </Box>
  </CajaTooltip>
);

interface SellerCardProps {
  seller: SellerDashboardSellerRow;
  onReassign: (clientId: number, currentSellerId: number) => void;
  hideUrgent?: boolean;
  showCrmUpload?: boolean;
}

const SellerCard: FC<SellerCardProps> = ({ seller, onReassign, hideUrgent = false, showCrmUpload = false }) => {
  const [open, setOpen] = useState(false);
  const urgentCount = useMemo(() => seller.leads.filter((l) => l.isUrgent && l.isOpen).length, [seller.leads]);
  const unansweredCount = seller.unansweredLeadCount;

  return (
    <Box
      sx={{
        mb: 2,
        borderRadius: RADIUS_CARD,
        background: SURFACE_CONTAINER_LOWEST,
        boxShadow: SHADOW,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          gap: { xs: 1.25, md: 2.5 },
          px: { xs: 2, md: 3 },
          py: { xs: 1.5, md: 1.75 },
          minWidth: 0,
          cursor: 'pointer',
          '&:hover': { background: SURFACE_TINT },
          transition: 'background 0.15s',
        }}
        onClick={() => setOpen((prev) => !prev)}
      >
        <Typography
          title={seller.name}
          sx={{
            fontFamily: FONT,
            fontWeight: 700,
            fontSize: { xs: '0.95rem', md: '1.05rem' },
            color: NAVY,
            flex: { xs: '1 1 100%', md: '1 1 auto' },
            minWidth: { xs: 0, md: 160 },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {seller.name}
        </Typography>

        <MetricBox
          label="Cierre Prom."
          value={seller.avgCloseTimeDays > 0 ? `${seller.avgCloseTimeDays} días` : '–'}
          tooltip="Promedio de días entre la asignación al ejecutivo y el cierre del lead."
        />

        <MetricBox
          label="1er Mensaje"
          value={formatMins(seller.avgFirstMessageMins)}
          tooltip="Tiempo promedio que el ejecutivo tarda en enviar su primer mensaje desde que el lead le fue asignado."
        />

        <MetricBox
          label="Resp. Prom."
          value={formatMins(seller.avgResponseTimeMins)}
          tooltip="Tiempo promedio que el ejecutivo tarda en responder."
        />

        <MetricBox
          label="Leads"
          value={String(seller.leadCount)}
          tooltip="Leads asignados al ejecutivo dentro del rango de fechas seleccionado."
        />

        <CajaTooltip
          title="Leads del ejecutivo dentro del período cuya conversación sigue activa (estado distinto de CLOSED)."
          arrow
          placement="top"
        >
          <Box
            sx={{
              flexShrink: 0,
              textAlign: 'center',
              background: seller.openLeadCount > 0 ? '#EEF0F4' : SURFACE_CONTAINER_LOW,
              borderRadius: '12px',
              px: 1,
              py: 0.8,
              minWidth: 84,
              cursor: 'help',
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: '0.6rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: seller.openLeadCount > 0 ? NAVY_MUTED : NAVY,
              }}
            >
              Abiertos
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: 800,
                fontSize: '1.4rem',
                color: seller.openLeadCount > 0 ? NAVY_MUTED : NAVY,
                lineHeight: 1.1,
              }}
            >
              {seller.openLeadCount}
            </Typography>
          </Box>
        </CajaTooltip>

        <CajaTooltip
          title="Leads abiertos del ejecutivo donde el cliente envió uno o más mensajes después de la última respuesta del ejecutivo."
          arrow
          placement="top"
        >
          <Box
            sx={{
              flexShrink: 0,
              textAlign: 'center',
              background: unansweredCount > 0 ? '#EEF0F4' : SURFACE_CONTAINER_LOW,
              borderRadius: '12px',
              px: 1,
              py: 0.8,
              minWidth: 84,
              cursor: 'help',
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: '0.6rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: unansweredCount > 0 ? NAVY_MUTED : NAVY,
              }}
            >
              Por responder
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: 800,
                fontSize: '1.4rem',
                color: unansweredCount > 0 ? NAVY_MUTED : NAVY,
                lineHeight: 1.1,
              }}
            >
              {unansweredCount}
            </Typography>
          </Box>
        </CajaTooltip>

        {!hideUrgent && (
          <CajaTooltip title={URGENT_RULES_TEXT} arrow placement="top">
            <Box
              sx={{
                flexShrink: 0,
                textAlign: 'center',
                background: urgentCount > 0 ? DANGER_BG : SURFACE_CONTAINER_LOW,
                borderRadius: '12px',
                px: 1,
                py: 0.8,
                minWidth: 84,
                cursor: 'help',
              }}
            >
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: urgentCount > 0 ? DANGER : NAVY,
                }}
              >
                Urgentes
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontWeight: 800,
                  fontSize: '1.4rem',
                  color: urgentCount > 0 ? DANGER : NAVY,
                  lineHeight: 1.1,
                }}
              >
                {urgentCount}
              </Typography>
            </Box>
          </CajaTooltip>
        )}

        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((prev) => !prev);
          }}
          sx={{
            flexShrink: 0,
            background: SURFACE_CONTAINER_LOW,
            borderRadius: RADIUS_ITEM,
            '&:hover': { background: SURFACE_HOVER },
          }}
        >
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box
          sx={{
            background: SURFACE,
            pt: 0.5,
            pb: 1.5,
          }}
        >
          {seller.leads.length === 0 ? (
            <Box sx={{ px: 3, py: 2 }}>
              <Typography sx={{ fontFamily: FONT, color: NAVY_FAINT, fontSize: '0.85rem' }}>
                No hay leads asignados a este vendedor en el período seleccionado.
              </Typography>
            </Box>
          ) : (
            <LeadsTable
              leads={seller.leads}
              onReassign={(clientId) => onReassign(clientId, seller.id)}
              hideUrgent={hideUrgent}
              showCrmUpload={showCrmUpload}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

interface SellerDashboardProps {
  businessUnitId: number;
  startDate: string;
  endDate: string;
  clientIdFilter?: string;
  statusFilter?: string;
  brands?: string[];
  branchOffices?: string[];
}

/**
 * SellerDashboard — per-seller metrics view.
 * Displays each seller's aggregate KPIs and their individual leads with timing metrics.
 */
const SellerDashboard: FC<SellerDashboardProps> = ({
  businessUnitId,
  startDate,
  endDate,
  clientIdFilter,
  statusFilter,
  brands,
  branchOffices,
}) => {
  const { data, isLoading, isFetching, isError, refetch } = useGetSellerDashboardQuery(
    {
      businessUnitId,
      startDate,
      endDate,
      brands,
      branchOffices,
    },
    { skip: !businessUnitId || businessUnitId === -1 }
  );

  const [getAssignableUsers, { data: assignableUsersResp, isLoading: isLoadingAssignable, reset: resetAssignable }] =
    useGetAssignableUsersMutation();
  const [assignClientToUser, { isLoading: isReassigning }] = useAssignClientToUserMutation();
  const [reassignTarget, setReassignTarget] = useState<{ clientId: number; currentSellerId: number } | null>(null);
  const [reassignError, setReassignError] = useState<string | null>(null);
  const [reassignSearch, setReassignSearch] = useState('');

  const handleReassign = (clientId: number, currentSellerId: number) => {
    setReassignError(null);
    setReassignSearch('');
    setReassignTarget({ clientId, currentSellerId });
    getAssignableUsers(clientId).catch(() => {
      /* handled by error state */
    });
  };

  const handleCloseReassignDialog = () => {
    setReassignTarget(null);
    setReassignError(null);
    setReassignSearch('');
    resetAssignable();
  };

  const handlePickNewSeller = async (newSellerId: number) => {
    if (!reassignTarget) return;
    try {
      await assignClientToUser({
        userId: newSellerId,
        clientToAssignId: reassignTarget.clientId,
      }).unwrap();
      handleCloseReassignDialog();
      await refetch();
    } catch (err) {
      const msg =
        (err as { data?: { message?: string } })?.data?.message ?? 'Error al reasignar el lead. Intenta nuevamente.';
      setReassignError(msg);
    }
  };

  const assignableOptions = useMemo(() => {
    const list = assignableUsersResp?.assignableUsers ?? [];
    const base = reassignTarget ? list.filter((u) => u.id !== reassignTarget.currentSellerId) : list;
    const q = reassignSearch.trim().toLowerCase();
    if (!q) return base;
    return base.filter((u) =>
      [u.name ?? '', u.email ?? '', u.branchOffice ?? ''].some((f) => f.toLowerCase().includes(q))
    );
  }, [assignableUsersResp, reassignTarget, reassignSearch]);

  const filteredSellers = useMemo(() => {
    const sellers = data?.sellers ?? [];
    const query = (clientIdFilter ?? '').trim().toLowerCase();
    const status = statusFilter ?? '';
    const needsLeadFilter = !!query || !!status;
    if (!needsLeadFilter) return sellers;
    return sellers
      .map((seller) => {
        const sellerNameMatches = query ? (seller.name ?? '').toLowerCase().includes(query) : false;
        const leads = seller.leads.filter((l) => {
          if (status === 'open' && !l.isOpen) return false;
          if (status === 'por-responder' && !(l.isOpen && l.unansweredClientMessageCount > 0)) return false;
          if (status === 'urgentes' && !(l.isOpen && l.isUrgent)) return false;
          if (query && !sellerNameMatches) {
            const haystack = [
              String(l.clientId ?? ''),
              l.clientName ?? '',
              l.clientPhone ?? '',
              l.clientEmail ?? '',
              l.clientRut ?? '',
            ]
              .join(' ')
              .toLowerCase();
            if (!haystack.includes(query)) return false;
          }
          return true;
        });
        return { ...seller, leads };
      })
      .filter((seller) => seller.leads.length > 0);
  }, [data?.sellers, clientIdFilter, statusFilter]);

  if (!businessUnitId || businessUnitId === -1) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          background: SURFACE,
          borderRadius: RADIUS_CARD,
        }}
      >
        <Typography sx={{ fontFamily: FONT, color: NAVY_FAINT, fontSize: '1rem' }}>
          Selecciona una unidad de negocio para ver las métricas por ejecutivo.
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} sx={{ color: YELLOW }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          background: DANGER_FAINT_BG,
          borderRadius: RADIUS_CARD,
        }}
      >
        <Typography sx={{ fontFamily: FONT, color: DANGER, fontSize: '1rem' }}>
          Error al cargar las métricas. Intenta nuevamente.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 1,
          px: { xs: 1, md: 2 },
          pb: 1,
        }}
      >
        <CajaTooltip
          title={isReassigning ? 'Reasignando lead…' : isFetching ? 'Actualizando…' : 'Actualizar datos'}
          arrow
          placement="top"
        >
          <span>
            <IconButton
              onClick={isFetching || isReassigning ? undefined : () => refetch()}
              disabled={isFetching || isReassigning}
              size="small"
              sx={{
                color: alpha(NAVY, 0.55),
                transition: 'all 0.15s ease-in-out',
                '&:hover': { bgcolor: alpha(NAVY, 0.06) },
              }}
            >
              {isReassigning || isFetching ? (
                <CircularProgress size={18} sx={{ color: alpha(NAVY, 0.55) }} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </CajaTooltip>
      </Box>
      {filteredSellers.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            background: SURFACE,
            borderRadius: RADIUS_CARD,
          }}
        >
          <Typography sx={{ fontFamily: FONT, color: NAVY_FAINT, fontSize: '1rem' }}>
            No hay ejecutivos con leads para los filtros aplicados.
          </Typography>
        </Box>
      ) : (
        filteredSellers.map((seller) => (
          <SellerCard
            key={seller.id}
            seller={seller}
            onReassign={handleReassign}
            hideUrgent={businessUnitId === INSUREX_BUSINESS_UNIT_ID}
            showCrmUpload={businessUnitId === SALFA_USED_ID}
          />
        ))
      )}

      <Dialog
        open={reassignTarget !== null}
        onClose={isReassigning ? undefined : handleCloseReassignDialog}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: RADIUS_CARD,
              backdropFilter: GLASS_BLUR,
              background: GLASS_BACKGROUND,
              boxShadow: SHADOW,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: FONT,
            fontWeight: 700,
            fontSize: '1.1rem',
            color: NAVY,
            pb: 1,
          }}
        >
          Reasignar lead
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {reassignError && (
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: '0.85rem',
                color: DANGER,
                mb: 1.5,
              }}
            >
              {reassignError}
            </Typography>
          )}

          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder="Buscar por nombre, email o sucursal"
            value={reassignSearch}
            onChange={(e) => setReassignSearch(e.target.value)}
            disabled={isReassigning || isLoadingAssignable}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: '1rem', color: NAVY_FAINT }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                fontFamily: FONT,
                fontSize: '0.88rem',
                borderRadius: '12px',
                background: SURFACE_TINT,
                '& fieldset': { borderColor: NAVY_HAIR },
                '&:hover fieldset': { borderColor: 'rgba(21, 24, 58, 0.35)' },
              },
            }}
          />

          {isLoadingAssignable ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} sx={{ color: YELLOW }} />
            </Box>
          ) : assignableOptions.length === 0 ? (
            <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', color: NAVY_FAINT, py: 1 }}>
              {reassignSearch.trim() ? 'Sin resultados.' : 'No hay ejecutivos disponibles para reasignar.'}
            </Typography>
          ) : (
            <List
              disablePadding
              sx={{
                maxHeight: 320,
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-thumb': { background: NAVY_HAIR, borderRadius: 3 },
              }}
            >
              {assignableOptions.map((u) => {
                const initials =
                  (u.name ?? u.email ?? '?')
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((p) => p[0]?.toUpperCase())
                    .join('') || '?';
                return (
                  <ListItemButton
                    key={u.id}
                    disabled={isReassigning}
                    onClick={() => handlePickNewSeller(u.id)}
                    sx={{
                      borderRadius: '12px',
                      mb: 0.5,
                      py: 1,
                      px: 1.25,
                      gap: 1.25,
                      display: 'flex',
                      alignItems: 'center',
                      '&:hover': { background: SURFACE_CONTAINER_LOW },
                      '&.Mui-disabled': { opacity: 0.5 },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        background: SURFACE_CONTAINER_LOW,
                        color: NAVY,
                        fontFamily: FONT,
                        fontSize: '0.82rem',
                        fontWeight: 700,
                      }}
                    >
                      {initials}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: FONT,
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: NAVY,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {u.name ?? u.email}
                      </Typography>
                      {(u.email || u.branchOffice) && (
                        <Typography
                          sx={{
                            fontFamily: FONT,
                            fontSize: '0.72rem',
                            color: NAVY_FAINT,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {[u.name ? u.email : null, u.branchOffice].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseReassignDialog}
            disabled={isReassigning}
            sx={{
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: '0.82rem',
              textTransform: 'none',
              borderRadius: RADIUS_COIN,
              color: NAVY,
              px: 2.5,
              py: 0.75,
              '&:hover': { background: SURFACE_TINT },
            }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SellerDashboard;
